import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  InputAdornment,
  Collapse,
  Tab,
  Tabs
} from '@mui/material';
import {
  Search,
  Sort,
  Add,
  Remove,
  LockOpen,
  Refresh,
  ExpandMore,
  ExpandLess,
  AccountBalance,
  Info,
  SwapHoriz,
  Send,
  History
} from '@mui/icons-material';
import APIService from '../../services/api-service';

const BalanceManagement = ({ user }) => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('siki');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [modifyDialog, setModifyDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [externalTransferDialog, setExternalTransferDialog] = useState(false);
  const [transferHistoryDialog, setTransferHistoryDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [transferHistory, setTransferHistory] = useState([]);

  // Modify balance form
  const [modifyForm, setModifyForm] = useState({
    coin: 'siki',
    amount: '',
    operation: 'add',
    reason: '',
    destinationWallet: ''
  });

  // Transfer forms
  const [transferForm, setTransferForm] = useState({
    fromWalletAddress: '',
    toWalletAddress: '',
    coin: 'siki',
    amount: '',
    reason: ''
  });

  const [externalTransferForm, setExternalTransferForm] = useState({
    fromWalletAddress: '',
    toWalletAddress: '',
    coin: 'siki',
    amount: '',
    reason: ''
  });

  // Debug user object
  console.log('BalanceManagement user object:', user);
  console.log('User email:', user?.email);

  // Check if user is chris@siki.io
  const isChris = user?.email === 'chris@siki.io';

  useEffect(() => {
    if (isChris) {
      fetchBalances();
    }
  }, [searchTerm, sortBy, sortOrder, page]);

  const fetchBalances = async () => {
    try {
      setLoading(true);
      const response = await APIService.getAllBalances({
        search: searchTerm,
        sortBy,
        order: sortOrder,
        page,
        limit: 50
      });
      setBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch balances');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await APIService.getUserBalanceDetails(userId);
      setUserDetails(response.data);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Failed to fetch user details');
    }
  };

  const handleModifyBalance = async () => {
    try {
      await APIService.modifyUserBalance(selectedUser.userId, modifyForm);
      alert(`Successfully ${modifyForm.operation}ed ${modifyForm.amount} ${modifyForm.coin.toUpperCase()}`);
      setModifyDialog(false);
      fetchBalances();
      setModifyForm({
        coin: 'siki',
        amount: '',
        operation: 'add',
        reason: '',
        destinationWallet: ''
      });
    } catch (error) {
      console.error('Failed to modify balance');
    }
  };

  const handleReleaseLock = async (userId, lockId) => {
    try {
      await APIService.releaseLock(userId, lockId);
      alert('Lock released successfully');
      fetchUserDetails(userId);
    } catch (error) {
      console.error('Failed to release lock');
    }
  };

  const handleReconcile = async (userId) => {
    try {
      const response = await APIService.reconcileBalance(userId);
      alert(response.message || 'Balance reconciled successfully');
      fetchBalances();
    } catch (error) {
      console.error('Failed to reconcile balance');
    }
  };

  // Transfer handlers
  const handleTransferTokens = async () => {
    try {
      const response = await APIService.transferTokens(transferForm);
      alert(response.message || 'Transfer completed successfully');
      setTransferDialog(false);
      fetchBalances();
      setTransferForm({
        fromWalletAddress: '',
        toWalletAddress: '',
        coin: 'siki',
        amount: '',
        reason: ''
      });
    } catch (error) {
      console.error('Failed to transfer tokens:', error);
      alert('Failed to transfer tokens: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleExternalTransfer = async () => {
    try {
      const response = await APIService.transferToExternalWallet(externalTransferForm);
      alert(response.message || 'External transfer completed successfully');
      setExternalTransferDialog(false);
      fetchBalances();
      setExternalTransferForm({
        fromWalletAddress: '',
        toWalletAddress: '',
        coin: 'siki',
        amount: '',
        reason: ''
      });
    } catch (error) {
      console.error('Failed to transfer to external wallet:', error);
      alert('Failed to transfer to external wallet: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchTransferHistory = async () => {
    try {
      const response = await APIService.getTransferHistory();
      setTransferHistory(response.data || []);
      setTransferHistoryDialog(true);
    } catch (error) {
      console.error('Failed to fetch transfer history:', error);
      alert('Failed to fetch transfer history');
    }
  };

  const openTransferDialog = (user) => {
    setTransferForm({
      ...transferForm,
      fromWalletAddress: user.accountId || ''
    });
    setTransferDialog(true);
  };

  const openExternalTransferDialog = (user) => {
    setExternalTransferForm({
      ...externalTransferForm,
      fromWalletAddress: user.accountId || ''
    });
    setExternalTransferDialog(true);
  };

  const formatBalance = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(value || 0);
  };

  const toggleRowExpansion = (userId) => {
    setExpandedRows(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  if (!isChris) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. This page is only accessible to chris@siki.io
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Balance Management
      </Typography>

      {/* Search and Filter Controls */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort By"
            >
              <MenuItem value="siki">SIKI Balance</MenuItem>
              <MenuItem value="hbar">HBAR Balance</MenuItem>
              <MenuItem value="matic">MATIC Balance</MenuItem>
              <MenuItem value="near">NEAR Balance</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              label="Order"
            >
              <MenuItem value="desc">High to Low</MenuItem>
              <MenuItem value="asc">Low to High</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={fetchBalances}
            startIcon={<Refresh />}
            sx={{ height: '56px' }}
          >
            Refresh
          </Button>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setTransferDialog(true)}
            startIcon={<SwapHoriz />}
            sx={{ height: '56px' }}
          >
            Transfer
          </Button>
        </Grid>
        <Grid item xs={12} md={1}>
          <Button
            fullWidth
            variant="text"
            onClick={fetchTransferHistory}
            startIcon={<History />}
            sx={{ height: '56px' }}
          >
            History
          </Button>
        </Grid>
      </Grid>

      {/* Balances Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell align="right">SIKI</TableCell>
                <TableCell align="right">HBAR</TableCell>
                <TableCell align="right">MATIC</TableCell>
                <TableCell align="right">NEAR</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {balances.map((balance) => {
                const hasNegativeBalance = 
                  balance.balances.siki.available < 0 ||
                  balance.balances.hbar.available < 0 ||
                  balance.balances.matic.available < 0 ||
                  balance.balances.near.available < 0;
                  
                return (
                  <React.Fragment key={balance.userId}>
                  <TableRow sx={{ 
                    backgroundColor: hasNegativeBalance ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                    '&:hover': {
                      backgroundColor: hasNegativeBalance ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={balance.profileImg} sx={{ width: 32, height: 32 }}>
                          {balance.name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{balance.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {balance.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography>{formatBalance(balance.balances.siki.total)}</Typography>
                        {balance.balances.siki.locked !== 0 && (
                          <Typography variant="caption" color="error">
                            Locked: {formatBalance(balance.balances.siki.locked)}
                          </Typography>
                        )}
                        {balance.balances.siki.available < 0 && (
                          <Typography variant="caption" color="error" fontWeight="bold">
                            Available: {formatBalance(balance.balances.siki.available)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography>{formatBalance(balance.balances.hbar.total)}</Typography>
                        {balance.balances.hbar.locked !== 0 && (
                          <Typography variant="caption" color="error">
                            Locked: {formatBalance(balance.balances.hbar.locked)}
                          </Typography>
                        )}
                        {balance.balances.hbar.available < 0 && (
                          <Typography variant="caption" color="error" fontWeight="bold">
                            Available: {formatBalance(balance.balances.hbar.available)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography>{formatBalance(balance.balances.matic.total)}</Typography>
                        {balance.balances.matic.locked !== 0 && (
                          <Typography variant="caption" color="error">
                            Locked: {formatBalance(balance.balances.matic.locked)}
                          </Typography>
                        )}
                        {balance.balances.matic.available < 0 && (
                          <Typography variant="caption" color="error" fontWeight="bold">
                            Available: {formatBalance(balance.balances.matic.available)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography>{formatBalance(balance.balances.near.total)}</Typography>
                        {balance.balances.near.locked !== 0 && (
                          <Typography variant="caption" color="error">
                            Locked: {formatBalance(balance.balances.near.locked)}
                          </Typography>
                        )}
                        {balance.balances.near.available < 0 && (
                          <Typography variant="caption" color="error" fontWeight="bold">
                            Available: {formatBalance(balance.balances.near.available)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => fetchUserDetails(balance.userId)}
                          >
                            <Info />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modify Balance">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedUser(balance);
                              setModifyDialog(true);
                            }}
                          >
                            <AccountBalance />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reconcile">
                          <IconButton
                            size="small"
                            onClick={() => handleReconcile(balance.userId)}
                          >
                            <Refresh />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Transfer to User">
                          <IconButton
                            size="small"
                            onClick={() => openTransferDialog(balance)}
                          >
                            <SwapHoriz />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Transfer to External">
                          <IconButton
                            size="small"
                            onClick={() => openExternalTransferDialog(balance)}
                          >
                            <Send />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modify Balance Dialog */}
      <Dialog open={modifyDialog} onClose={() => setModifyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modify Balance</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <>
              <Box mb={2}>
                <Typography variant="subtitle2">
                  User: {selectedUser.name} ({selectedUser.email})
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={modifyForm.coin}
                      onChange={(e) => setModifyForm({ ...modifyForm, coin: e.target.value })}
                      label="Currency"
                    >
                      <MenuItem value="siki">SIKI</MenuItem>
                      <MenuItem value="hbar">HBAR</MenuItem>
                      <MenuItem value="matic">MATIC</MenuItem>
                      <MenuItem value="near">NEAR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Operation</InputLabel>
                    <Select
                      value={modifyForm.operation}
                      onChange={(e) => setModifyForm({ ...modifyForm, operation: e.target.value })}
                      label="Operation"
                    >
                      <MenuItem value="add">Add</MenuItem>
                      <MenuItem value="subtract">Subtract</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={modifyForm.amount}
                    onChange={(e) => setModifyForm({ ...modifyForm, amount: e.target.value })}
                  />
                </Grid>
                {modifyForm.operation === 'subtract' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Destination Wallet (for subtract operations)"
                      placeholder="Enter wallet address/account ID"
                      value={modifyForm.destinationWallet}
                      onChange={(e) => setModifyForm({ ...modifyForm, destinationWallet: e.target.value })}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    multiline
                    rows={3}
                    value={modifyForm.reason}
                    onChange={(e) => setModifyForm({ ...modifyForm, reason: e.target.value })}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModifyDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleModifyBalance}
            disabled={!modifyForm.amount || !modifyForm.reason}
          >
            {modifyForm.operation === 'add' ? 'Add' : 'Subtract'} Balance
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Balance Details</Typography>
            <IconButton onClick={() => setDetailsDialog(false)}>
              ×
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {userDetails && (
            <>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab label="Balance Overview" />
                <Tab label="Locked Funds" />
                <Tab label="Recent Transactions" />
              </Tabs>

              {/* Balance Overview Tab */}
              {activeTab === 0 && (
                <Grid container spacing={2}>
                  {['siki', 'hbar', 'matic', 'near'].map((coin) => (
                    <Grid item xs={12} sm={6} key={coin}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            {coin.toUpperCase()}
                          </Typography>
                          <Typography>
                            Total: {formatBalance(userDetails.balances[coin].total)}
                          </Typography>
                          <Typography color="error">
                            Locked: {formatBalance(userDetails.balances[coin].locked)}
                          </Typography>
                          <Typography color="success.main">
                            Available: {formatBalance(userDetails.balances[coin].available)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Locked Funds Tab */}
              {activeTab === 1 && (
                <Box>
                  {['tip', 'auction', 'cashout', 'other'].map((type) => (
                    <Box key={type} mb={2}>
                      <Typography variant="h6" gutterBottom>
                        {type.charAt(0).toUpperCase() + type.slice(1)} Locks
                      </Typography>
                      {userDetails.lockedBreakdown[type].entries.length === 0 ? (
                        <Typography color="text.secondary">No {type} locks</Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Amount</TableCell>
                                <TableCell>Currency</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Expires</TableCell>
                                <TableCell>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {userDetails.lockedBreakdown[type].entries.map((entry) => (
                                <TableRow key={entry.id}>
                                  <TableCell>{formatBalance(entry.amount)}</TableCell>
                                  <TableCell>{entry.coin.toUpperCase()}</TableCell>
                                  <TableCell>
                                    {new Date(entry.createdAt).toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    {entry.expiresAt
                                      ? new Date(entry.expiresAt).toLocaleString()
                                      : 'No expiry'}
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleReleaseLock(userDetails.user.userId, entry.id)
                                      }
                                    >
                                      <LockOpen />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Recent Transactions Tab */}
              {activeTab === 2 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Currency</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userDetails.recentTransactions.map((tx) => (
                        <TableRow key={tx._id}>
                          <TableCell>{new Date(tx.createdAt).toLocaleString()}</TableCell>
                          <TableCell>{tx.type}</TableCell>
                          <TableCell>
                            {tx.fromUser === userDetails.user.userId ? '-' : '+'}
                            {formatBalance(tx.amount)}
                          </TableCell>
                          <TableCell>{tx.currency?.toUpperCase()}</TableCell>
                          <TableCell>
                            <Chip
                              label={tx.status}
                              size="small"
                              color={tx.status === 'approved' ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Token Transfer Dialog */}
      <Dialog open={transferDialog} onClose={() => setTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Tokens Between Wallets</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="From Wallet Address"
                placeholder="Enter from wallet address (e.g., 0.0.12345)"
                value={transferForm.fromWalletAddress}
                onChange={(e) => setTransferForm({ ...transferForm, fromWalletAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="To Wallet Address"
                placeholder="Enter recipient wallet address (e.g., 0.0.67890)"
                value={transferForm.toWalletAddress}
                onChange={(e) => setTransferForm({ ...transferForm, toWalletAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={transferForm.coin}
                  onChange={(e) => setTransferForm({ ...transferForm, coin: e.target.value })}
                  label="Currency"
                >
                  <MenuItem value="siki">SIKI</MenuItem>
                  <MenuItem value="hbar">HBAR</MenuItem>
                  <MenuItem value="matic">MATIC</MenuItem>
                  <MenuItem value="near">NEAR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={transferForm.amount}
                onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={transferForm.reason}
                onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransferTokens}
            disabled={!transferForm.fromWalletAddress || !transferForm.toWalletAddress || !transferForm.amount || !transferForm.reason}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* External Transfer Dialog */}
      <Dialog open={externalTransferDialog} onClose={() => setExternalTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Tokens to External Wallet</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="From Wallet Address"
                placeholder="Enter from wallet address (e.g., 0.0.12345)"
                value={externalTransferForm.fromWalletAddress}
                onChange={(e) => setExternalTransferForm({ ...externalTransferForm, fromWalletAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="External Wallet Address"
                placeholder="Enter external wallet address (e.g., 0.0.67890)"
                value={externalTransferForm.toWalletAddress}
                onChange={(e) => setExternalTransferForm({ ...externalTransferForm, toWalletAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={externalTransferForm.coin}
                  onChange={(e) => setExternalTransferForm({ ...externalTransferForm, coin: e.target.value })}
                  label="Currency"
                >
                  <MenuItem value="siki">SIKI</MenuItem>
                  <MenuItem value="hbar">HBAR</MenuItem>
                  <MenuItem value="matic">MATIC</MenuItem>
                  <MenuItem value="near">NEAR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={externalTransferForm.amount}
                onChange={(e) => setExternalTransferForm({ ...externalTransferForm, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={externalTransferForm.reason}
                onChange={(e) => setExternalTransferForm({ ...externalTransferForm, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExternalTransferDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExternalTransfer}
            disabled={!externalTransferForm.fromWalletAddress || !externalTransferForm.toWalletAddress || !externalTransferForm.amount || !externalTransferForm.reason}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer History Dialog */}
      <Dialog open={transferHistoryDialog} onClose={() => setTransferHistoryDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Transfer History</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>From Wallet</TableCell>
                  <TableCell>To Wallet</TableCell>
                  <TableCell>From User</TableCell>
                  <TableCell>To User</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Admin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transferHistory.map((transfer) => (
                  <TableRow key={transfer._id}>
                    <TableCell>{new Date(transfer.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={transfer.transferType || 'internal'}
                        color={transfer.transferType === 'external' ? 'secondary' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transfer.fromWalletAddress || 'N/A'}</TableCell>
                    <TableCell>{transfer.toWalletAddress || 'N/A'}</TableCell>
                    <TableCell>{transfer.fromUserEmail || 'Unknown'}</TableCell>
                    <TableCell>{transfer.toUserEmail || 'External'}</TableCell>
                    <TableCell>{transfer.coin?.toUpperCase()}</TableCell>
                    <TableCell align="right">{formatBalance(transfer.amount)}</TableCell>
                    <TableCell>{transfer.reason}</TableCell>
                    <TableCell>{transfer.adminEmail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BalanceManagement;